import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { captureException } from '@sentry/react';
import type { AxiosResponse } from 'axios';

import { authorizedAxiosInstance } from '@/clients';

import type { TranscriptExecutive, TranscriptPeriod } from './types';
import {
    getTranscriptExecutives,
    getTranscriptPeriod,
    getTranscriptText,
    normalizeFmpCollection,
    parseTranscriptSections,
} from './utils';

interface UseTranscriptWorkspaceDataOptions {
    initialQuarter?: number;
    initialYear?: number;
    symbol: string;
}

const samePeriod = (left?: TranscriptPeriod, right?: TranscriptPeriod) =>
    left?.quarter === right?.quarter && left?.year === right?.year;

export const useTranscriptWorkspaceData = ({
    initialQuarter,
    initialYear,
    symbol,
}: UseTranscriptWorkspaceDataOptions) => {
    const initialPeriodRef = useRef({ quarter: initialQuarter, year: initialYear });
    const [periods, setPeriods] = useState<TranscriptPeriod[]>([]);
    const [activePeriod, setActivePeriodState] = useState<TranscriptPeriod>();
    const [executives, setExecutives] = useState<TranscriptExecutive[]>([]);
    const [transcriptText, setTranscriptText] = useState('');
    const [loadingPeriods, setLoadingPeriods] = useState(true);
    const [loadingTranscript, setLoadingTranscript] = useState(false);

    useEffect(() => {
        const controller = new AbortController();

        const loadMetadata = async () => {
            if (!symbol) {
                setPeriods([]);
                setActivePeriodState(undefined);
                setExecutives([]);
                setLoadingPeriods(false);
                return;
            }

            setLoadingPeriods(true);

            try {
                const [periodResponse, executiveResponse] = await Promise.all([
                    authorizedAxiosInstance.get<unknown, AxiosResponse<unknown>>(
                        `/api/vendors/fmp/transcripts/${encodeURIComponent(symbol)}/dates`,
                        { signal: controller.signal },
                    ),
                    authorizedAxiosInstance.get<unknown, AxiosResponse<unknown>>(
                        '/api/vendors/fmp/governance/executives',
                        { params: { ticker: symbol }, signal: controller.signal },
                    ),
                ]);
                const nextPeriods = normalizeFmpCollection(periodResponse.data)
                    .map(getTranscriptPeriod)
                    .filter((period): period is TranscriptPeriod => Boolean(period));
                const requestedPeriod = nextPeriods.find(
                    (period) =>
                        period.quarter === initialPeriodRef.current.quarter &&
                        period.year === initialPeriodRef.current.year,
                );

                setPeriods(nextPeriods);
                setActivePeriodState(
                    (current) =>
                        nextPeriods.find((period) => samePeriod(period, current)) ?? requestedPeriod ?? nextPeriods[0],
                );
                setExecutives(getTranscriptExecutives(executiveResponse.data));
            } catch (error) {
                if (!controller.signal.aborted) {
                    captureException(error);
                    setPeriods([]);
                    setActivePeriodState(undefined);
                    setExecutives([]);
                }
            } finally {
                if (!controller.signal.aborted) {
                    setLoadingPeriods(false);
                }
            }
        };

        void loadMetadata();

        return () => controller.abort();
    }, [symbol]);

    useEffect(() => {
        const controller = new AbortController();

        const loadTranscript = async () => {
            if (!symbol || !activePeriod) {
                setTranscriptText('');
                setLoadingTranscript(false);
                return;
            }

            setLoadingTranscript(true);
            setTranscriptText('');

            try {
                const response = await authorizedAxiosInstance.get<unknown, AxiosResponse<unknown>>(
                    `/api/vendors/fmp/transcripts/${encodeURIComponent(symbol)}`,
                    {
                        params: { quarter: activePeriod.quarter, year: activePeriod.year },
                        signal: controller.signal,
                    },
                );
                const transcript = normalizeFmpCollection(response.data)[0];

                setTranscriptText(getTranscriptText(transcript));
            } catch (error) {
                if (!controller.signal.aborted) {
                    captureException(error);
                    setTranscriptText('');
                }
            } finally {
                if (!controller.signal.aborted) {
                    setLoadingTranscript(false);
                }
            }
        };

        void loadTranscript();

        return () => controller.abort();
    }, [activePeriod, symbol]);

    const sections = useMemo(() => parseTranscriptSections(transcriptText), [transcriptText]);
    const setActivePeriod = useCallback((period: TranscriptPeriod) => {
        setActivePeriodState((current) => (samePeriod(current, period) ? current : period));
    }, []);

    return {
        activePeriod,
        executives,
        loadingPeriods,
        loadingTranscript,
        periods,
        sections,
        setActivePeriod,
        transcriptText,
    };
};
